import { Alchemy, OwnedNft, OwnedNftsResponse } from 'alchemy-sdk';
import camelCase from 'lodash/camelCase';
import { PublicClient } from 'viem';

import {
  AvatarEvmNft,
  EVMNft,
  EvmNFTChain,
  EvmNFTCollection,
  EvmNFTCollectionContractName,
} from '@repo/types-client';

import { AvatarABI, PassABI } from '../abi';
import { baseContractAddresses } from '../data';
import {
  baseAlchemyClient,
  BaseAlchemyProvider,
  ethereumAlchemyClient,
  EthereumAlchemyProvider,
} from '../providers';
import { convertAlchemyNftToEVMNft } from './convert-alchemy-nft-to-evm-nft';
import { getEvmNFTCollectionByContractName } from './get-evm-nft-collection-by-contract-name';

/**
 * Base interface for fetching EVM NFTs from a blockchain
 */
interface GetEvmNftsFromChainInput {
  chain: EvmNFTChain;
  addresses?: undefined;
  address?: undefined;
  tokenId?: undefined;
}

/**
 * Interface for fetching a single EVM NFT by token ID
 */
export interface GetEvmNftsFromChainSingularInput
  extends Omit<GetEvmNftsFromChainInput, 'tokenId'> {
  tokenId: string;
  collections: [EvmNFTCollectionContractName];
}

/**
 * Interface for fetching a single ERC1155 NFT by token ID and address
 */
export interface GetEvmNftsFromChainSingularERC1155Input
  extends Omit<GetEvmNftsFromChainInput, 'tokenId' | 'address'> {
  tokenId: string;
  address: string | undefined;
  collections: [EvmNFTCollectionContractName];
}

/**
 * Interface for fetching multiple NFTs by addresses
 */
export interface GetEvmNftsFromChainPluralInput
  extends Omit<GetEvmNftsFromChainInput, 'addresses'> {
  addresses: string[];
  collections: EvmNFTCollectionContractName[];
}

export function getEVMNftsFromChain<T extends EVMNft>(
  props: GetEvmNftsFromChainSingularInput,
): Promise<T>;

export function getEVMNftsFromChain<T extends EVMNft>(
  props: GetEvmNftsFromChainSingularERC1155Input,
): Promise<T>;

export function getEVMNftsFromChain<T extends EVMNft>(
  props: GetEvmNftsFromChainPluralInput,
): Promise<T[]>;

/**
 * Fetches NFT data from the specified blockchain
 * @param input - Configuration object containing chain, addresses, and collection info
 * @returns Promise resolving to either a single NFT or array of NFTs
 */
export async function getEVMNftsFromChain<T extends EVMNft>(
  input:
    | GetEvmNftsFromChainSingularInput
    | GetEvmNftsFromChainSingularERC1155Input
    | GetEvmNftsFromChainPluralInput,
): Promise<T | T[]> {
  const { address, addresses, chain, tokenId, collections } = input;

  const alchemyClient =
    chain === 'ethereum' ? ethereumAlchemyClient : baseAlchemyClient;
  const alchemyProvider =
    chain === 'ethereum' ? EthereumAlchemyProvider : BaseAlchemyProvider;

  const nftCollections = collections.map((collection) =>
    getEvmNFTCollectionByContractName(collection),
  );

  const evmContractAddresses = nftCollections
    .filter((collection) => collection?.chain === chain)
    .map((collection) => collection?.getAddress());

  if (!evmContractAddresses.length) {
    return [];
  }

  let nfts: EVMNft[] = [];

  // Fetch a single NFT (ERC-1155 not supported)
  if (tokenId !== undefined && !address) {
    const nft = (await getAlchemyNFT(
      alchemyClient,
      tokenId,
      nftCollections[0]!,
    )) as T;

    nfts.push(nft);
  }

  // Fetch a single ERC-1155 NFT
  if (tokenId !== undefined && address) {
    const nft = (await getAlchemyNFTERC1155(
      alchemyClient,
      address,
      tokenId,
      nftCollections[0]!,
    )) as T;

    nfts.push(nft);
  }

  // Fetch NFTs from multiple addresses
  if (addresses) {
    for (const address of addresses) {
      const evmNfts = await getAlchemyNFTs(
        alchemyClient,
        address,
        evmContractAddresses,
      );
      nfts.push(...evmNfts);
    }
  }

  // Manually fetch avatars metadata to avoid Alchemy cache
  nfts = await overrideMetadataForAvatars(
    nfts,
    alchemyProvider as PublicClient,
  );

  const response = tokenId !== undefined ? nfts[0] : nfts;

  return response as T | T[];
}

/**
 * Fetches multiple NFTs owned by an address using Alchemy API
 * @param alchemyClient - Initialized Alchemy client
 * @param address - Owner's wallet address
 * @param contractAddresses - Array of NFT contract addresses to query
 * @returns Promise resolving to array of EVMNft objects
 */
async function getAlchemyNFTs(
  alchemyClient: Alchemy,
  address: string,
  contractAddresses: string[],
): Promise<EVMNft[]> {
  let pageKey: string | undefined = undefined;
  const alchemyNfts: OwnedNft[] = [];

  do {
    const response: OwnedNftsResponse = await alchemyClient.nft.getNftsForOwner(
      address,
      {
        contractAddresses,
        pageKey,
      },
    );
    alchemyNfts.push(...response.ownedNfts);

    pageKey = response.pageKey;
    // Alchemy docs for response.pageKey:
    // Pagination token that can be passed into another request to fetch the next NFTs.
    // If there is no page key, then there are no more NFTs to fetch.
  } while (pageKey);

  const evmNfts = alchemyNfts.map((alchemyNft) =>
    convertAlchemyNftToEVMNft({
      alchemyNft,
      owner: address,
    }),
  );

  return evmNfts;
}

/**
 * Fetches metadata for a single non-ERC1155 NFT
 * @param alchemyClient - Initialized Alchemy client
 * @param tokenId - ID of the NFT token
 * @param collection - Collection metadata object
 * @throws Error if collection is ERC1155 type
 * @returns Promise resolving to EVMNft object
 */
async function getAlchemyNFT(
  alchemyClient: Alchemy,
  tokenId: string,
  collection: EvmNFTCollection,
): Promise<EVMNft> {
  if (collection.tokenType === 'ERC1155') {
    throw new Error('Invalid NFT collection');
  }

  const [nft, ownerForNft] = await Promise.all([
    alchemyClient.nft.getNftMetadata(collection.getAddress(), tokenId),
    alchemyClient.nft.getOwnersForNft(collection.getAddress(), tokenId),
  ]);

  if (!ownerForNft.owners[0]) {
    throw new Error('No owner found for NFT');
  }

  const evmNft = convertAlchemyNftToEVMNft({
    alchemyNft: nft as OwnedNft,
    owner: ownerForNft.owners[0],
  });

  return evmNft;
}

/**
 * Fetches metadata for a single ERC1155 NFT
 * @param alchemyClient - Initialized Alchemy client
 * @param address - Owner's wallet address
 * @param tokenId - ID of the NFT token
 * @param collection - Collection metadata object
 * @throws Error if collection is not ERC1155 type
 * @returns Promise resolving to EVMNft object with balance
 */
async function getAlchemyNFTERC1155(
  alchemyClient: Alchemy,
  address: string,
  tokenId: string,
  collection: EvmNFTCollection,
): Promise<EVMNft> {
  if (collection.tokenType !== 'ERC1155') {
    throw new Error('Invalid NFT collection');
  }

  const [nft, balance] = await Promise.all([
    alchemyClient.nft.getNftMetadata(collection.getAddress(), tokenId),
    BaseAlchemyProvider.readContract({
      address: collection.getAddress(),
      abi: PassABI, // Any ERC1155 ABI will work
      functionName: 'balanceOf',
      args: [address, tokenId],
    }),
  ]);

  const evmNft = convertAlchemyNftToEVMNft({
    alchemyNft: nft as OwnedNft,
    owner: address,
  });

  evmNft.balance = String(balance);

  return evmNft;
}

/**
 * Updates metadata for Avatar NFTs by fetching fresh data from the blockchain
 * This bypasses Alchemy's cache to ensure up-to-date information
 * @param nfts - Array of NFTs to process
 * @param publicClient - Initialized blockchain client
 * @returns Promise resolving to updated array of NFTs
 */
async function overrideMetadataForAvatars(
  nfts: EVMNft[],
  publicClient: PublicClient,
): Promise<EVMNft[]> {
  const avatarAddress = baseContractAddresses.avatar;
  for (const nft of nfts) {
    if (nft.contract.address.toLowerCase() !== avatarAddress.toLowerCase()) {
      continue;
    }

    const tokenUri = await publicClient.readContract({
      address: avatarAddress,
      abi: AvatarABI,
      functionName: 'tokenURI',
      args: [BigInt(nft.tokenId)],
    });

    const tokenUriBase64 = (tokenUri as string)?.split('base64,')[1];

    if (!tokenUriBase64) {
      throw new Error('Invalid token URI format');
    }

    const decodedTokenUri = atob(tokenUriBase64);

    const metadata = JSON.parse(decodedTokenUri);

    nft.name = metadata.name;
    nft.description = metadata.description;
    nft.image = {
      originalUrl: metadata.image,
    };
    nft.tokenUri = tokenUri as string;
    nft.structuredAttributes =
      metadata.attributes.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (acc: any, attribute: Record<string, any>) => {
          acc[camelCase(attribute.trait_type)] = attribute.value;
          return acc;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {} as Record<string, any>,
      ) || {};
    nft.metadata = {
      name: metadata.name,
      image: metadata.image,
      description: metadata.description,
      animation_url: metadata.animation_url,
      attributes: metadata.attributes,
    };

    const equippedWearables = metadata.children.wearables.map(
      (wearable: { token_id: string; template_id: string }) => ({
        tokenId: wearable.token_id,
        templateId: wearable.template_id,
        avatarId: nft.tokenId,
      }),
    );

    (nft as AvatarEvmNft).equippedWearables = equippedWearables;
  }

  return nfts;
}
